import { ChainId } from '@uniswap/sdk-core'
import { useEffect, useState, useMemo } from 'react'
import { ConnectedWallet, usePrivy, useWallets } from '@privy-io/react-auth'
import { ethers } from 'ethers'

interface StoredWallet {
  address: string
  walletType: string
  connectedAt: number
}

export function useActiveWeb3React() {
  const { wallets, ready: walletsReady } = useWallets()
  const { authenticated, ready: privyReady } = usePrivy()
  const [provider, setProvider] = useState<ethers.providers.Web3Provider | null>(null)

  // Get the first wallet from the list (active wallet)
  const activeWallet = useMemo(() => {
    if (!wallets) return undefined
    return [...wallets].sort((a, b) => {
      const timeA = a.connectedAt || 0
      const timeB = b.connectedAt || 0
      return timeB - timeA
    })[0]
  }, [wallets])

  // Get chain ID from active wallet
  const chainId = useMemo(() => activeWallet?.chainId.replace('eip155:', ''), [activeWallet])
  const wrongChain = chainId !== String(ChainId.HYPEREVM)

  // Initialize provider when current wallet changes
  useEffect(() => {
    if (!activeWallet) {
      setProvider(null)
      return
    }

    let mounted = true

    activeWallet
      .getEthereumProvider()
      .then((ethProvider) => {
        if (mounted) {
          setProvider(new ethers.providers.Web3Provider(ethProvider as any))
        }
      })
      .catch((error) => {
        console.error('Failed to get provider:', error)
        if (mounted) {
          setProvider(null)
        }
      })

    return () => {
      mounted = false
    }
  }, [activeWallet])

  return {
    account: authenticated ? activeWallet?.address : undefined,
    active: authenticated,
    chainId: chainId ? parseInt(chainId) : undefined,
    wrongChain,
    wallet: authenticated ? activeWallet : undefined,
    library: provider,
    walletsReady,
    privyReady,
  }
}

export const getLastConnectedWallet = (wallets: ConnectedWallet[]): ConnectedWallet | undefined => {
  try {
    const storedWallet = localStorage.getItem('lastConnectedWallet')
    if (!storedWallet) return undefined

    const storedWalletParsed: StoredWallet = JSON.parse(storedWallet)
    if (!storedWalletParsed?.address || !storedWalletParsed?.walletType) return undefined

    // Find the wallet that matches both address and type
    return wallets.find(
      (wallet) =>
        wallet.address.toLowerCase() === storedWalletParsed.address.toLowerCase() &&
        wallet.meta?.name === storedWalletParsed.walletType
    )
  } catch (error) {
    console.error('Error retrieving last connected wallet:', error)
    return undefined
  }
}
